import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton, useAsync } from '../../components/ui';
import { Enrollment, ENROLLMENT_STATUS, EXIT_REASON, Graph, RunLogEntry } from '../../journeys/model';
import { RunLogTimeline } from '../../journeys/RunLogTimeline';
import { api, Paged } from '../../lib/api';
import { ago, dateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { errorText } from '../Contacts';

export function EnrollmentPill({ e }: { e: Enrollment }) {
  const s = ENROLLMENT_STATUS[e.status];
  return <span className={`pill ${s.tone}`}>{t(s.label)}{e.exit_reason ? `: ${t(EXIT_REASON[e.exit_reason] ?? e.exit_reason)}` : ''}</span>;
}

/** The log of one enrollment with links to its emails. */
export function EnrollmentLog({ journeyId, enrollmentId }: { journeyId: string; enrollmentId: string }) {
  const r = useAsync(() => api.get<{ enrollment: Enrollment; log: RunLogEntry[] }>(`/journeys/${journeyId}/enrollments/${enrollmentId}/log`), [journeyId, enrollmentId]);
  if (r.loading && !r.data) return <Skeleton h={160} />;
  if (r.error) return <div className="callout bad">{errorText(r.error)}</div>;
  const e = r.data!.enrollment;
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: 8 }}>
        <EnrollmentPill e={e} />
        <span className="small muted">v{e.version} · {e.group === 'control' ? t('контрольная группа') : t('получает письма')} · {t('вошёл {dt}', { dt: dateTime(e.entered_at) })}</span>
        {Object.entries(e.assignments ?? {}).map(([k, v]) => <span key={k} className="small">{t('сплит')} <code>{k}</code>: <b>{v}</b></span>)}
        {e.goal_reached_at && <span className="pill ok">{t('цель {ago}', { ago: ago(e.goal_reached_at) })}</span>}
      </div>
      <RunLogTimeline log={r.data!.log} messageLink={(id) => <Link to={`/emails/${id}`}><code>{id}</code></Link>} />
    </div>
  );
}

/** «Участия»: the drops of a journey with filters; a click opens the run log (docs/journeys/SPEC.md §4.1). */
export function JourneyRunLog({ journeyId, graph, initial }: { journeyId: string; graph: Graph | null; initial?: string | null }) {
  const [status, setStatus] = useState('');
  const [step, setStep] = useState('');
  const [items, setItems] = useState<Enrollment[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<string | null>(initial ?? null);

  async function load(next: string | null) {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50', ...(status ? { status } : {}), ...(step ? { step } : {}), ...(next ? { cursor: next } : {}) });
      const r = await api.get<Paged<Enrollment>>(`/journeys/${journeyId}/enrollments?${p}`);
      setItems((xs) => (next ? [...xs, ...r.items] : r.items));
      setCursor(r.next_cursor);
      setError('');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load(null);
  }, [journeyId, status, step]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grid grid-2">
      <div className="card" style={{ minWidth: 0 }}>
        <div className="card-pad row wrap" style={{ gap: 8 }}>
          <select className="select input" style={{ width: 'auto' }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('Статус участия')}>
            <option value="">{t('все статусы')}</option>
            {Object.entries(ENROLLMENT_STATUS).map(([k, v]) => <option key={k} value={k}>{t(v.label)}</option>)}
          </select>
          <select className="select input" style={{ width: 'auto' }} value={step} onChange={(e) => setStep(e.target.value)} aria-label={t('Текущий шаг')}>
            <option value="">{t('любой шаг')}</option>
            {Object.keys(graph?.steps ?? {}).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {error && <div className="card-pad callout bad">{error}</div>}
        {loading && !items.length ? (
          <div className="card-pad"><Skeleton /></div>
        ) : !items.length ? (
          <div className="card-pad small muted">{t('Участий нет.')}</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('Контакт')}</th><th>{t('Статус')}</th><th>{t('Шаг')}</th><th>{t('Вошёл')}</th></tr></thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className={`click${open === e.id ? ' on' : ''}`} onClick={() => setOpen(e.id)} style={open === e.id ? { background: 'var(--surface-3)' } : undefined}>
                    <td className="small" style={{ overflowWrap: 'anywhere' }}>{e.email ?? <code>{e.contact_id}</code>}{e.group === 'control' && <div className="muted">{t('контроль')}</div>}</td>
                    <td><EnrollmentPill e={e} /></td>
                    <td className="small"><code>{e.current_step ?? '—'}</code></td>
                    <td className="small muted" title={dateTime(e.entered_at)}>{ago(e.entered_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor && <div className="card-pad"><button className="btn btn-sm" disabled={loading} onClick={() => load(cursor)}>{t('Показать ещё')}</button></div>}
      </div>
      <div className="card" style={{ minWidth: 0 }}>
        <div className="card-head"><h3>{t('Погружение капли')}</h3></div>
        <div className="card-pad">{open ? <EnrollmentLog journeyId={journeyId} enrollmentId={open} /> : <span className="small muted">{t('Выберите участие слева — покажем каждый шаг с временем и исходом.')}</span>}</div>
      </div>
    </div>
  );
}
